// src/admin/components/UserFormModal.jsx
//
// Create/edit a user. Replaces the always-open inline form at the top of the
// old UsersPage — username, email, and the role checkbox grid — now behind
// the header's action button, matching accounts/components/
// RegisterAccountModal.jsx and Connections/components/RegisterConnectionModal.jsx.
//
// size="lg": the role list needs room for a name plus what it grants, but not
// the full-width "xl" (see RoleFormModal's note on why xl is both widest and
// shortest).
//
// No password field, deliberately — identity comes from Google OAuth. An
// admin pre-creates the record and its roles; the email is what the person's
// first login is matched against.

import { useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { Modal, FormField, Input, Button, Alert, SkeletonText, useDiscardConfirm } from "../../shared/ui";
import RolePicker from "./RolePicker";

export default function UserFormModal({
  open,
  user,
  roles,
  rolesLoading,
  rolesError,
  onClose,
  onSubmit,
}) {
  const isEdit = Boolean(user);

  const [values, setValues] = useState({
    username: user?.username || "",
    email: user?.email || "",
    roleIds: (user?.roles || []).map((r) => r.id),
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);

  function handleClose() {
    setErrors({});
    setSaved(null);
    setSubmitting(false);
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !saved, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const nextErrors = {};
    if (!values.username.trim()) nextErrors.username = "Username is required.";
    if (!values.email.trim()) {
      nextErrors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
      // Shape only. The EmailAddress value object owns real validation and
      // normalisation server-side; this just avoids a round trip on a typo.
      nextErrors.email = "Enter a valid email address.";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await onSubmit({
        username: values.username.trim(),
        email: values.email.trim(),
        roleIds: values.roleIds,
      });
      setSaved(result);
    } catch (err) {
      // A 409/422 arrives with fieldErrors from toAdminError (admin/api/
      // adminApiError.js) — email is the unique column, so "already exists"
      // lands under that input. Anything else is a banner.
      if (err?.fieldErrors) setErrors(err.fieldErrors);
      else setErrors({ _form: err?.message || "Save failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  // Only on edit: an empty selection sends clear_roles=true, stripping every
  // role. On create there's nothing to lose, so it's not worth warning about.
  const willRemoveAllRoles = isEdit && values.roleIds.length === 0 && user.roles.length > 0;

  return (
    <>
      <Modal
        open={open}
        size="lg"
        title={isEdit ? `Edit user — ${user.username}` : "New user"}
        onClose={requestClose}
      >
        {saved ? (
          <div className="flex flex-col gap-4">
            <Alert
              variant="success"
              icon={CheckCircle2}
              title={isEdit ? `"${saved.username}" updated` : `"${saved.username}" created`}
            >
              {isEdit
                ? "Their access changes the next time they sign in — the current session keeps its existing permissions."
                : `They can sign in with Google as ${saved.email}, and that login will be matched to this record.`}
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {errors._form && (
              <Alert variant="danger" icon={XCircle} title="Couldn't save">
                {errors._form}
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Username"
                required
                helperText="Display name shown throughout the app."
                error={Boolean(errors.username)}
                errorText={errors.username}
              >
                <Input
                  value={values.username}
                  onChange={(e) => setField("username", e.target.value)}
                  placeholder="e.g. jane.doe"
                  autoFocus
                />
              </FormField>

              <FormField
                label="Email"
                required
                helperText="Their Google account address — that's how the first login matches this record."
                error={Boolean(errors.email)}
                errorText={errors.email}
              >
                <Input
                  type="email"
                  value={values.email}
                  onChange={(e) => setField("email", e.target.value)}
                  placeholder="e.g. jane@company.com"
                />
              </FormField>
            </div>

            <FormField
              label="Roles"
              helperText="Saving replaces this user's roles with exactly this selection."
            >
              {rolesLoading ? (
                <div className="p-3 border border-line rounded-card">
                  <SkeletonText lines={4} />
                </div>
              ) : rolesError ? (
                <Alert variant="danger" icon={XCircle} title="Couldn't load assignable roles">
                  {rolesError} Close this dialog and retry — saving now would clear
                  this user&apos;s existing roles.
                </Alert>
              ) : (
                <RolePicker
                  roles={roles}
                  value={values.roleIds}
                  onChange={(next) => setField("roleIds", next)}
                />
              )}
            </FormField>

            {willRemoveAllRoles && (
              <Alert variant="warning" icon={AlertTriangle} title="This will remove every role">
                {user.username} currently holds{" "}
                {user.roles.map((r) => r.name).join(", ")}. Saving with nothing selected
                leaves them able to sign in but failing every permission check.
              </Alert>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                // Submitting while the role list failed to load would send
                // clear_roles=true and silently strip the user's roles.
                disabled={rolesLoading || Boolean(rolesError)}
              >
                {isEdit ? "Save changes" : "Create user"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

UserFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** null → create mode; a record → edit mode, seeded from it. */
  user: PropTypes.shape({
    id: PropTypes.number,
    username: PropTypes.string,
    email: PropTypes.string,
    roles: PropTypes.array,
  }),
  roles: PropTypes.array.isRequired,
  rolesLoading: PropTypes.bool,
  rolesError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
